import { ActionButton } from '../components/ActionButton';
import { useControllerConnectionStatus } from '../Hooks/useControllerConnectionStatus';
import { getPhysicalIOLabel } from '../Utils/physical_io_label';
import React, { useState, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { isMobile } from 'react-device-detect';
import { Line } from 'react-chartjs-2';
import { makeStyles } from '@material-ui/core/styles';
import { useTranslation } from 'react-i18next';
import Typography from '@material-ui/core/Typography';
import Grid from '@material-ui/core/Grid';
import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import MuiSwitch from '@material-ui/core/Switch';
import DialogWithEnterAction from '../components/DialogWithEnterAction';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import TableCell from '@material-ui/core/TableCell';
import TableRow from '@material-ui/core/TableRow';

import getStateManager from '../StateManager.js';
import { useSnapshot } from 'valtio';

import MultiOutLabel, { MultiOutHeading } from '../FormWidgets/MultiOutLabel';
import {
  IrrigationStatusBuffer,
  NormalValue,
  NegativeValue,
  FloatValue,
  ExternalReadings
} from '../FormWidgets/InfoValues';
import Desc from '../FormWidgets/Description';
import {
  offOnMapping,
  dayNightCo2PreheatMapping,
  onOffMapping,
  irrigationStateMapping,
  irrigationPhaseMapping,
  irrigationErrorMapping,
  irrigationErrorMapping5055,
  hvacInitStatusMapping,
  hvacStatusMapping,
  hvacOperationModeMapping,
  co2CylinderValveMapping,
  co2CylinderStatusMapping,
  sensor_value_type_mapping,
  ahu_type_mapping,
  ahu_info_init_state_mapping,
  ahu_info_operational_state_mapping,
  ahu_info_temp_air_info_type_mapping,
  multiIOHwTypeMappping,
  multiIOMapping
} from '../Constants/constants';
import { SettingGroup, ValueBox } from '../FormWidgets/SettingGroup';
import {
  makeMultiOutObject,
  makeSlaveInfoObject,
  makeExtReadingsObject,
  makeAHUInfoObject,
  makeAHUUnitObject,
  IsLegacyGrowBaseProWifiApi,
  IsExcludedBasedOnTypeOrProject,
  isZoned
} from '../Utils/utils.js';
import { settingControls, writeTempSettings } from '../Tabs/settingFunctions';
import { getRootZoneIrrigationShowCounts, hasIrrigationError } from '../Utils/irrigation_utils.js';
import { getPaddedGraphBounds, getValidGraphValues } from '../Utils/chart_utils';
import {
  getAvailableSettingNumber,
  isMultiIOInfoVisible,
  getHeatingMatSetpoint,
  getVpdSetpoint
} from '../Utils/info_utils';
import {
  formatGraphAge,
  formatSelectedGraphSpan,
  getInfoMiniGraphGeometry,
  getRenderableGraphPoints
} from '../Utils/info_graph_utils';

import { ConnectionStatus } from '../FormWidgets/SlaveWidgets.js';
import MultiIOInfoCard from '../FormWidgets/MultiIOInfoCard.js';
import NutritionTankInfo, { getNutritionTankInfo } from './NutritionTankInfo';
import { tankOwnsSource, tankSourceZone } from '../Utils/tank_monitoring';
import { supportsNutritionTanks } from '../Utils/nutrition_utils';
import MultiIOInfoTableRow from '../FormWidgets/MultiIOInfoTableRow.js';

import { CustomizableComponent, CustomizableScope } from '../FormWidgets/CustomizableComponent';
import {
  CustomizableInfoValueRow,
  CustomizableInfoValueCollection,
  InfoValueBox,
  InfoValueDashboard,
  InfoValueGroup,
  InfoValueList,
  InfoValueRowsGrid,
  splitInfoRowsIntoColumns
} from '../FormWidgets/InfoValueList';
import {
  MULTI_IO_SHORT_LABEL,
  MultiIOTable,
  MultiIOViewToggle,
  sortMultiIORows,
  useMultiIOTableSort,
  useMultiIOViewMode
} from '../components/MultiIOView.js';
import useInfoClimateGraphs from '../Hooks/useInfoClimateGraphs.js';
import useInfoChartData from '../Hooks/useInfoChartData.js';
import {
  rootZoneInfoGraphKeys,
  subscribeToInfoGraphChanges
} from '../Utils/info_graph_subscription.js';

const useStyles = makeStyles((theme) => ({
  root: {
    flexGrow: 1
  },
  graphData: {
    width: '100%',
    height: '100%'
  },
  graphFrame: {
    display: 'grid',
    gridTemplateColumns: 'max-content minmax(0, 1fr)',
    gridTemplateRows: '1fr 1fr',
    columnGap: theme.spacing(1),
    boxSizing: 'border-box',
    width: '100%',
    minWidth: 0,
    '& $graphSpan': {
      gridColumn: 1,
      gridRow: '1 / 3',
      alignSelf: 'center',
      justifySelf: 'start'
    }
  },
  climateGraphFrame: {
    height: 120
  },
  miniGraphFrame: {
    height: 42,
    paddingRight: theme.spacing(2),
    [theme.breakpoints.down('xs')]: {
      paddingRight: theme.spacing(1.5)
    }
  },
  graphPlot: {
    gridColumn: 2,
    gridRow: '1 / 3',
    minWidth: 0,
    minHeight: 0,
    position: 'relative'
  },
  graphMaximum: {
    gridColumn: 1,
    gridRow: 1,
    alignSelf: 'start'
  },
  graphMinimum: {
    gridColumn: 1,
    gridRow: 2,
    alignSelf: 'end'
  },
  graphSpan: {
    whiteSpace: 'nowrap'
  },
  compactClimateItem: {
    display: 'flex',
    '& > *': {
      width: '100%'
    }
  },
  setpointInfo: {
    color: theme.palette.text.secondary,
    fontSize: '0.8rem'
  },
  multiOutBox: {
    backgroundColor: theme.palette.innerContainer2.background
  },
  icon: {
    color: theme.palette.primary.main
  }
}));

function InfoGraphFrame({ children, className = '', maximum, minimum, span }) {
  const classes = useStyles();

  return (
    <div className={`${classes.graphFrame} ${className}`}>
      <Typography className={`${classes.setpointInfo} ${classes.graphMaximum}`}>
        {maximum}
      </Typography>
      <div className={classes.graphPlot}>{children}</div>
      <Typography className={`${classes.setpointInfo} ${classes.graphSpan}`}>{span}</Typography>
      <Typography className={`${classes.setpointInfo} ${classes.graphMinimum}`}>
        {minimum}
      </Typography>
    </div>
  );
}

const InfoMiniGraph = React.memo(function InfoMiniGraph({ digits, indices, span, values }) {
  const classes = useStyles();
  const geometry = getInfoMiniGraphGeometry(indices, values);
  if (!geometry) return null;

  const { points, minX, maxX, minY, maxY } = geometry;
  const xSpan = Math.max(1, maxX - minX);
  const ySpan = Math.max(1, maxY - minY);
  const svgPoints = points
    .map(({ x, y }) => {
      const svgX = 2 + ((x - minX) / xSpan) * 116;
      const svgY = 40 - ((y - minY) / ySpan) * 38;
      return `${svgX.toFixed(1)},${svgY.toFixed(1)}`;
    })
    .join(' ');

  return (
    <InfoGraphFrame
      className={classes.miniGraphFrame}
      maximum={maxY.toFixed(digits)}
      minimum={minY.toFixed(digits)}
      span={span}
    >
      <svg
        aria-hidden="true"
        focusable="false"
        preserveAspectRatio="none"
        style={{ display: 'block', height: '100%', width: '100%' }}
        viewBox="0 0 120 42"
      >
        <polyline
          fill="none"
          points={svgPoints}
          stroke="#95c11e"
          strokeLinejoin="round"
          strokeWidth="2"
          vectorEffect="non-scaling-stroke"
        />
      </svg>
    </InfoGraphFrame>
  );
});

const StableInfoLine = React.memo(function StableInfoLine({ data, options }) {
  return <Line data={data} options={options} />;
});

function readRootZoneGraphData(infos, index) {
  return {
    indices: Array.from(infos.root_zone_sample_indices ?? []),
    vwc: Array.from(infos.vol_water_content_graphs?.[index] ?? []),
    rootEc: Array.from(infos.ec_root_zone_graphs?.[index] ?? []),
    span: formatSelectedGraphSpan(
      infos.root_zone_sample_indices,
      Number(infos.info_graph_source_sample_count) || 0,
      Number(infos.info_graph_sample_interval_seconds) || 0
    )
  };
}

function RootZoneIrrigationPair({
  controller,
  parent,
  index,
  curValues,
  extReadsValues,
  getExtReads,
  irrigationErrorLabelMapping,
  irrigationStatusZoneName,
  openExtReadsDialog,
  orphan = false,
  renderGraphs = true,
  showIrrigation,
  showRootZone,
  paired = showRootZone && showIrrigation
}) {
  const { t } = useTranslation();
  const number = index + 1;
  const suffix = index === 0 ? '' : `_${number}`;
  const rootHeading = index === 0 ? 'root_zone_hl' : `root_zone_hl_${number}`;
  const irrigationHeading = index === 0 ? 'irrigation_info_hl' : `irrigation_info_hl_${number}`;
  const value = (key, fallback = -1) => curValues[`${key}${suffix}`] ?? fallback;
  const [graphData, setGraphData] = useState(() => readRootZoneGraphData(parent.infos, index));
  const alignRootZoneValues =
    getRenderableGraphPoints(graphData.indices, graphData.vwc).length >= 3 ||
    getRenderableGraphPoints(graphData.indices, graphData.rootEc).length >= 3;

  useEffect(() => {
    const updateGraphData = () => setGraphData(readRootZoneGraphData(parent.infos, index));
    updateGraphData();
    return subscribeToInfoGraphChanges(parent.infos, rootZoneInfoGraphKeys, updateGraphData);
  }, [index, parent.infos]);

  const externalReadings = (type, key, ValueComp, digits) => {
    const readings = getExtReads(type, index);
    if (!isZoned(controller) || readings.length === 0) return null;

    return (
      <ExternalReadings
        expand_id={`info_ext_read_${key}${suffix}`}
        readings={readings}
        extReadsValues={extReadsValues}
        namePrefix={`ext_read_${key}${suffix}_`}
        ValueComp={ValueComp}
        num_digits={digits}
        marginProps={0}
        valueProps={{ fontSize: '0.9rem' }}
        onAdd={() => openExtReadsDialog(type, index)}
      />
    );
  };

  return (
    <CustomizableInfoValueCollection
      controller={controller}
      id={`root_zone_irrigation_collection_${number}`}
      orphan={orphan}
      paired={paired}
      parent={parent}
    >
      {showRootZone && (
        <CustomizableComponent
          controller={controller}
          parent={parent}
          id={rootHeading}
          ext_id={`edit_${rootHeading}`}
          visibilityIconPosition="heading-end"
          zIndex="-1"
        >
          <InfoValueGroup title={t(rootHeading)}>
            <CustomizableInfoValueRow
              controller={controller}
              parent={parent}
              id="info_vol_water_content"
              extId="edit_info_vol_water_content"
              label={<Desc id="vol_water_content" />}
              fixedVisualization={renderGraphs && alignRootZoneValues}
              visualization={
                renderGraphs ? (
                  <InfoMiniGraph
                    digits={0}
                    indices={graphData.indices}
                    span={graphData.span}
                    values={graphData.vwc}
                  />
                ) : null
              }
              valueDetail={externalReadings(
                sensor_value_type_mapping.SENS_VAL_ROOT_VWC,
                'vol_water_content',
                FloatValue,
                1
              )}
            >
              <FloatValue name="vol_water_content" value={value('vol_water_content')} unit="%" />
            </CustomizableInfoValueRow>
            <CustomizableInfoValueRow
              controller={controller}
              parent={parent}
              id="info_ec_root_zone"
              extId="edit_info_ec_root_zone"
              label={<Desc id="ec_root_zone" />}
              fixedVisualization={renderGraphs && alignRootZoneValues}
              visualization={
                renderGraphs ? (
                  <InfoMiniGraph
                    digits={1}
                    indices={graphData.indices}
                    span={graphData.span}
                    values={graphData.rootEc}
                  />
                ) : null
              }
              valueDetail={externalReadings(
                sensor_value_type_mapping.SENS_VAL_ROOT_EC_PW,
                'ec_root_zone',
                FloatValue,
                2
              )}
            >
              <FloatValue
                name="ec_root_zone"
                value={value('ec_root_zone')}
                unit="mS/cm"
                num_digits={2}
              />
            </CustomizableInfoValueRow>
            <CustomizableInfoValueRow
              controller={controller}
              parent={parent}
              id="info_temp_root_zone"
              extId="edit_info_temp_root_zone"
              label={<Desc id="temp_root_zone" />}
              fixedVisualization={alignRootZoneValues}
              valueDetail={externalReadings(
                sensor_value_type_mapping.SENS_VAL_ROOT_TEMP,
                'temp_root_zone',
                FloatValue,
                1
              )}
            >
              <FloatValue name="temp_root_zone" value={value('temp_root_zone')} unit="°C" />
            </CustomizableInfoValueRow>
          </InfoValueGroup>
        </CustomizableComponent>
      )}

      {showIrrigation && (
        <CustomizableComponent
          controller={controller}
          parent={parent}
          id={irrigationHeading}
          ext_id={`edit_${irrigationHeading}`}
          visibilityIconPosition="heading-end"
          zIndex="-1"
        >
          <InfoValueGroup title={t(irrigationHeading)}>
            <CustomizableInfoValueRow
              controller={controller}
              parent={parent}
              id="irrigation_state"
              extId="edit_info_irrigation_state"
              label={<Desc id="irrigation_state" />}
              detail={
                <IrrigationStatusBuffer
                  value={value('irrigation_status_buffer', '')}
                  zoneName={irrigationStatusZoneName}
                />
              }
            >
              <NormalValue
                name="irrigation_state"
                value={t(
                  irrigationStateMapping[value('irrigation_state')] ??
                    irrigationStateMapping.default
                )}
                unit=""
              />
            </CustomizableInfoValueRow>
            <CustomizableInfoValueRow
              controller={controller}
              parent={parent}
              id="irrigation_phase_info"
              extId="edit_info_irrigation_phase_info"
              label={<Desc id="irrigation_phase_info" />}
            >
              <NormalValue
                name="irrigation_phase_info"
                value={t(
                  irrigationPhaseMapping[value('irrigation_phase_info')] ??
                    irrigationPhaseMapping.default
                )}
                unit=""
              />
            </CustomizableInfoValueRow>
            <CustomizableInfoValueRow
              controller={controller}
              parent={parent}
              id="irrigation_err"
              extId="edit_info_irrigation_err"
              label={<Desc id="irrigation_err" />}
              error={hasIrrigationError(controller, value('irrigation_err'))}
            >
              <NormalValue
                name="irrigation_err"
                value={t(
                  irrigationErrorLabelMapping[value('irrigation_err')] ??
                    irrigationErrorLabelMapping.default
                )}
                unit=""
              />
            </CustomizableInfoValueRow>
          </InfoValueGroup>
        </CustomizableComponent>
      )}
    </CustomizableInfoValueCollection>
  );
}

function NutrientsInfoPanel({
  controller,
  parent,
  settings,
  index,
  curValues,
  extReadsValues,
  getExtReads,
  openExtReadsDialog
}) {
  const { t } = useTranslation();
  const number = index + 1;
  const suffix = index === 0 ? '' : `_${number}`;
  const heading = index === 0 ? 'nutrients_hl' : `nutrients_hl_${number}`;
  const value = (key) => curValues[`${key}${suffix}`] ?? -1;
  const nutrientSettings =
    settings?.nutrition?.nutrient_settings?.[index] ??
    (index === 0 ? settings?.nutrition : undefined);
  const setpoint = (key, unit) => {
    const rawValue = getAvailableSettingNumber(nutrientSettings?.[key]);
    if (rawValue == null) return null;

    return `${t('setpoint_info')} ${(Number(rawValue) / 100).toFixed(2)} ${unit}`;
  };

  const externalReadings = (type, key, ValueComp, digits) => {
    const readings = getExtReads(type, index);
    if (!isZoned(controller) || readings.length === 0) return null;

    return (
      <ExternalReadings
        expand_id={`info_ext_read_${key}${suffix}`}
        readings={readings}
        extReadsValues={extReadsValues}
        namePrefix={`ext_read_${key}${suffix}_`}
        ValueComp={ValueComp}
        num_digits={digits}
        marginProps={0}
        valueProps={{ fontSize: '0.9rem' }}
        onAdd={() => openExtReadsDialog(type, index)}
      />
    );
  };

  const rows = [
    {
      id: 'ec_nutrient_solution',
      type: sensor_value_type_mapping.SENS_VAL_NUTRI_EC,
      ValueComp: FloatValue,
      digits: 2,
      unit: 'mS/cm',
      setpointKey: 'ec_setpoint'
    },
    {
      id: 'ph_nutrient_solution',
      type: sensor_value_type_mapping.SENS_VAL_NUTRI_PH,
      ValueComp: FloatValue,
      digits: 2,
      unit: 'pH',
      setpointKey: 'ph_setpoint'
    },
    {
      id: 'temp_nutrient_solution',
      type: sensor_value_type_mapping.SENS_VAL_NUTRI_TEMP,
      ValueComp: FloatValue,
      digits: 1,
      unit: '°C'
    },
    {
      id: 'redox_current',
      type: sensor_value_type_mapping.SENS_VAL_NUTRI_REDOX,
      ValueComp: NormalValue,
      digits: 0,
      unit: 'mV'
    },
    {
      id: 'dos_current',
      type: sensor_value_type_mapping.SENS_VAL_NUTRI_DOS,
      ValueComp: FloatValue,
      digits: 1,
      unit: '%'
    }
  ];

  return (
    <CustomizableComponent
      controller={controller}
      parent={parent}
      id={heading}
      ext_id={`edit_${heading}`}
      visibilityIconPosition="heading-end"
      zIndex="-1"
    >
      <InfoValueGroup title={t(heading)}>
        {rows.map(({ id, type, ValueComp, digits, unit, setpointKey }) => (
          <CustomizableInfoValueRow
            key={id}
            controller={controller}
            parent={parent}
            id={id}
            extId={`edit_info_${id}`}
            label={<Desc id={id} />}
            setpoint={setpointKey ? setpoint(setpointKey, unit) : null}
            valueDetail={externalReadings(type, id, ValueComp, digits)}
          >
            <ValueComp
              name={id}
              value={value(id)}
              unit={unit}
              {...(ValueComp === FloatValue ? { num_digits: digits } : {})}
            />
          </CustomizableInfoValueRow>
        ))}
      </InfoValueGroup>
    </CustomizableComponent>
  );
}

function HvacInfoPanel({ controller, parent, index, curValues }) {
  const { t } = useTranslation();
  const number = index + 1;
  const suffix = index === 0 ? '' : `_${number}`;
  const heading = index === 0 ? 'hvac_info_hl' : `hvac_info_hl_${number}`;
  const value = (key) => curValues[`${key}${suffix}`] ?? -1;
  const unitType = curValues[`hvac_unit_type${suffix}`];

  const commonRows = [
    {
      id: 'hvac_status',
      value: t(hvacStatusMapping[value('hvac_status')] ?? hvacStatusMapping.default)
    }
  ];
  const defaultRows = [
    ...commonRows,
    { id: 'hvac_rh_info', value: value('hvac_rh_info'), unit: '% RH', float: true },
    { id: 'hvac_temp_info', value: value('hvac_temp_info'), unit: '°C', float: true },
    {
      id: 'hvac_compress_status',
      value: t(onOffMapping[value('hvac_compress_status')] ?? onOffMapping.default)
    },
    { id: 'hvac_info', value: value('hvac_info') }
  ];
  const rows =
    unitType === 2
      ? [
          {
            id: 'hvac_init_status',
            value: t(
              hvacInitStatusMapping[value('hvac_init_status')] ?? hvacInitStatusMapping.default
            )
          },
          ...commonRows,
          {
            id: 'hvac_temp_setpoint_trans',
            value: value('hvac_temp_setpoint_trans'),
            unit: '°C',
            float: true
          },
          {
            id: 'hvac_op_mode_info',
            value: t(
              hvacOperationModeMapping[value('hvac_op_mode_info')] ??
                hvacOperationModeMapping.default
            )
          },
          { id: 'hvac_info', value: value('hvac_info') }
        ]
      : defaultRows;

  return (
    <CustomizableComponent
      controller={controller}
      parent={parent}
      id={heading}
      ext_id={`edit_${heading}`}
      visibilityIconPosition="heading-end"
      zIndex="-1"
    >
      <InfoValueGroup title={t(heading)}>
        {rows.map(({ id, value: rowValue, unit = '', float = false }) => (
          <CustomizableInfoValueRow
            key={id}
            controller={controller}
            parent={parent}
            id={id}
            extId={`edit_info_${id}`}
            label={<Desc id={id} />}
          >
            {float ? (
              <FloatValue name={id} value={rowValue} unit={unit} />
            ) : (
              <NormalValue name={id} value={rowValue} unit={unit} />
            )}
          </CustomizableInfoValueRow>
        ))}
      </InfoValueGroup>
    </CustomizableComponent>
  );
}

function Co2InfoPanel({ controller, parent, values }) {
  const { t } = useTranslation();
  const rows = [
    {
      id: 'co2_cylinder_current_valve',
      customId: 'co2_hl_info_co2_cylinder_current_valve',
      value: t(
        co2CylinderValveMapping[values.co2_cylinder_current_valve ?? -1] ??
          co2CylinderValveMapping.default
      )
    },
    {
      id: 'co2_cylinder_1_status',
      customId: 'co2_hl_co2_cylinder_1_status',
      value: t(
        co2CylinderStatusMapping[values.co2_cylinder_1_status ?? -1] ??
          co2CylinderStatusMapping.default
      )
    },
    {
      id: 'co2_cylinder_2_status',
      customId: 'co2_hl_co2_cylinder_2_status',
      value: t(
        co2CylinderStatusMapping[values.co2_cylinder_2_status ?? -1] ??
          co2CylinderStatusMapping.default
      )
    }
  ];

  return (
    <CustomizableComponent
      controller={controller}
      parent={parent}
      id="co2_hl"
      ext_id="edit_co2_hl"
      visibilityIconPosition="heading-end"
      zIndex="-1"
    >
      <InfoValueGroup title={t('co2_hl')}>
        {rows.map(({ id, customId, value }) => (
          <CustomizableInfoValueRow
            key={id}
            controller={controller}
            parent={parent}
            id={customId}
            extId={`edit_info_${id}`}
            label={<Desc id={id} />}
          >
            <NormalValue name={id} value={value} unit="" />
          </CustomizableInfoValueRow>
        ))}
      </InfoValueGroup>
    </CustomizableComponent>
  );
}

function AhuInfoPanel({ ahuUnits, controller, index, parent, storageScope = '', unit }) {
  const { t } = useTranslation();
  const number = index + 1;
  const heading = `ahu_hl_${number}`;
  const expandId = storageScope
    ? `${storageScope}:info_ahu_more_${unit.ahu_id ?? index}`
    : `info_ahu_more_${index}`;
  const isVallox = unit.type_id === ahu_type_mapping.AHU_TYPE_VALLOX_RD5;
  const isFanMultiIO = unit.type_id === ahu_type_mapping.AHU_TYPE_FAN_MULTI_IO;
  const [expanded, setExpanded] = useState(() => {
    const savedState = localStorage.getItem(`expandable_${expandId}`);
    return savedState === 'true';
  });

  useEffect(() => {
    localStorage.setItem(`expandable_${expandId}`, expanded.toString());
  }, [expandId, expanded]);

  if (!isFanMultiIO && !isVallox) return null;

  const name = ahuUnits.ahus.find((ahu) => ahu.ahu_id === unit.ahu_id)?.name ?? '---';
  const commonRows = [
    { id: 'ahu_unit_name', value: name },
    {
      id: 'fan_power_current',
      value: unit.fan_power_current ?? -1,
      unit: '%',
      valueProps: { off_at: 0, off_label: t('off') }
    },
    {
      id: 'bypass_position_current',
      value: unit.bypass_position_current ?? -1,
      unit: '%',
      valueProps: {
        off_at: 0,
        off_label: t('closed'),
        bypass_at: 100,
        bypass_label: t('bypass')
      }
    }
  ];
  const valloxRows = [
    ...commonRows,
    {
      id: 'init_state',
      value: t(
        ahu_info_init_state_mapping[unit.init_state ?? -1] ?? ahu_info_init_state_mapping.default
      )
    },
    {
      id: 'operational_state',
      value: t(
        ahu_info_operational_state_mapping[unit.operational_state ?? -1] ??
          ahu_info_operational_state_mapping.default
      )
    },
    {
      id: 'temp_air_info',
      labelId: t(
        ahu_info_temp_air_info_type_mapping[unit.temp_air_info_type ?? -1] ??
          ahu_info_temp_air_info_type_mapping.default
      ),
      value: unit.temp_air_info ?? -1,
      unit: '°C',
      float: true
    }
  ];
  const detailRows = [
    { id: 'temp_air_supply', value: unit.temp_air_supply ?? -1, unit: '°C', float: true },
    { id: 'temp_air_exhaust', value: unit.temp_air_exhaust ?? -1, unit: '°C', float: true },
    { id: 'temp_air_outdoor', value: unit.temp_air_outdoor ?? -1, unit: '°C', float: true },
    { id: 'temp_air_indoor', value: unit.temp_air_indoor ?? -1, unit: '°C', float: true },
    { id: 'temp_air_extract', value: unit.temp_air_extract ?? -1, unit: '°C', float: true },
    {
      id: 'flow_air_supply',
      value: unit.flow_air_supply ?? -1,
      unit: '%',
      valueProps: { off_at: 0, off_label: t('off') }
    },
    {
      id: 'flow_air_extract',
      value: unit.flow_air_extract ?? -1,
      unit: '%',
      valueProps: { off_at: 0, off_label: t('off') }
    },
    { id: 'alarm_code', value: unit.alarm_code ?? -1 },
    { id: 'warning_code', value: unit.warning_code ?? -1 },
    { id: 'filter_hours_current', value: unit.filter_hours_current ?? -1, unit: 'h' },
    { id: 'filter_hours_threshold', value: unit.filter_hours_threshold ?? -1, unit: 'h' },
    {
      id: 'heat_recovery_efficiency_percent',
      value: unit.heat_recovery_efficiency_percent ?? -1,
      unit: '%'
    }
  ];

  const renderRow = ({ id, labelId = id, value, unit: valueUnit = '', float, valueProps }) => (
    <CustomizableInfoValueRow
      key={id}
      controller={controller}
      parent={parent}
      id={`info_${id}_${number}`}
      extId={id === 'temp_air_exhaust' ? `${id}_${number}` : `edit_${id}_${number}`}
      label={<Desc id={labelId} />}
    >
      {float ? (
        <FloatValue name={`${id}_${number}`} value={value} unit={valueUnit} />
      ) : (
        <NormalValue name={`${id}_${number}`} value={value} unit={valueUnit} {...valueProps} />
      )}
    </CustomizableInfoValueRow>
  );

  const title = (
    <span style={{ alignItems: 'center', display: 'inline-flex' }}>
      {t(heading)}
      {isVallox && (
        <ConnectionStatus
          ahu_status={unit.slave_status}
          controllerStatus={controller?.connection?.status}
        />
      )}
    </span>
  );
  const visibleRows = isVallox ? valloxRows : commonRows;
  const toggleRow = isVallox ? (
    <CustomizableInfoValueRow
      controller={controller}
      parent={parent}
      id={`info_more_${number}`}
      extId=""
      label={t('more')}
    >
      <Button
        aria-expanded={expanded}
        aria-label={t('more')}
        color="primary"
        onClick={() => setExpanded((value) => !value)}
        size="small"
      >
        {expanded ? '▲' : '▼'}
      </Button>
    </CustomizableInfoValueRow>
  ) : null;
  const detailColumnCapacity = visibleRows.length + (toggleRow ? 1 : 0);
  const detailColumns = splitInfoRowsIntoColumns(detailRows, detailColumnCapacity).map((rows) =>
    rows.map(renderRow)
  );

  return (
    <CustomizableComponent
      controller={controller}
      parent={parent}
      id={heading}
      ext_id={`edit_${heading}`}
      visibilityIconPosition="heading-end"
      zIndex="-1"
    >
      <InfoValueGroup title={title} expanded={isVallox && expanded}>
        {isVallox && expanded ? (
          <InfoValueRowsGrid
            columns={[[...visibleRows.map(renderRow), toggleRow], ...detailColumns]}
          />
        ) : (
          <>
            {visibleRows.map(renderRow)}
            {toggleRow}
          </>
        )}
      </InfoValueGroup>
    </CustomizableComponent>
  );
}

function renderAhuInfoCollections({ controller, filterByZone = true, parent, storageScope = '' }) {
  const ahuInfos = makeAHUInfoObject(controller, parent, filterByZone);
  const ahuUnits = makeAHUUnitObject(controller, parent, filterByZone);

  return (ahuInfos.ahui ?? [])
    .slice()
    .sort((a, b) => a.ahu_id - b.ahu_id)
    .map(
      (unit, index) =>
        unit.show_this_unit === 1 && (
          <CustomizableInfoValueCollection
            controller={controller}
            id={`ahu_collection_${unit.ahu_id ?? index + 1}`}
            key={`ahu-${unit.ahu_id ?? index}`}
            parent={parent}
          >
            <AhuInfoPanel
              ahuUnits={ahuUnits}
              controller={controller}
              index={index}
              parent={parent}
              storageScope={storageScope}
              unit={unit}
            />
          </CustomizableInfoValueCollection>
        )
    );
}

export function GlobalInfoContent({ controller, customizationScope = 'overview:global' }) {
  const snapshot = useSnapshot(controller);
  const tankCount = getNutritionTankInfo(snapshot, snapshot).length;
  return (
    <CustomizableScope scope={customizationScope}>
      <InfoValueDashboard>
        {tankCount > 0 && (
          <NutritionTankInfo controller={controller} parent={controller} paired={tankCount > 1} />
        )}
        {renderAhuInfoCollections({
          controller,
          filterByZone: false,
          parent: controller,
          storageScope: customizationScope
        })}
      </InfoValueDashboard>
    </CustomizableScope>
  );
}

function ExternalReadingsDialog({
  open,
  handleClose,
  write_access,
  setExtReadsValues,
  extReadsControls,
  transform_sent_settings,
  extReadsCurValues,
  extReadsValues,
  selectedSubZone,
  selectedType,
  controller,
  mobile,
  tankName
}) {
  const { status: connectionStatus, disabledReason: connectionDisabledReason } =
    useControllerConnectionStatus(controller);

  let { t } = useTranslation();

  // Build titles
  const climateValuesTitle = (() => {
    return t('climate_values');
  })();
  const rootZoneTitle = (() => {
    return selectedSubZone === 0 ? t('root_zone_hl') : t(`root_zone_hl_${selectedSubZone + 1}`);
  })();
  const nutrientsTitle = (() => {
    if (tankName) return tankName;
    return selectedSubZone === 0 ? t('nutrients_hl') : t(`nutrients_hl_${selectedSubZone + 1}`);
  })();

  // Type to label by sensor type
  const typeToLabel = {
    // Climate
    [sensor_value_type_mapping.SENS_VAL_RH]: `${climateValuesTitle} • ${t('humidity')}`,
    [sensor_value_type_mapping.SENS_VAL_TEMP_AIR]: `${climateValuesTitle} • ${t('temp')}`,
    [sensor_value_type_mapping.SENS_VAL_LEAF_T]: `${climateValuesTitle} • ${t('temp_leaf_current')}`,
    [sensor_value_type_mapping.SENS_VAL_CO2]: `${climateValuesTitle} • ${t('co2')}`,
    // Root Zone (Sub-zonable)
    [sensor_value_type_mapping.SENS_VAL_ROOT_VWC]: `${rootZoneTitle} • ${t('vol_water_content')}`,
    [sensor_value_type_mapping.SENS_VAL_ROOT_EC_PW]: `${rootZoneTitle} • ${t('ec_root_zone')}`,
    [sensor_value_type_mapping.SENS_VAL_ROOT_TEMP]: `${rootZoneTitle} • ${t('temp_root_zone')}`,
    // Nutrients (Sub-zonable)
    [sensor_value_type_mapping.SENS_VAL_NUTRI_EC]: `${nutrientsTitle} • ${t('ec_nutrient_solution')}`,
    [sensor_value_type_mapping.SENS_VAL_NUTRI_PH]: `${nutrientsTitle} • ${t('ph_nutrient_solution')}`,
    [sensor_value_type_mapping.SENS_VAL_NUTRI_TEMP]: `${nutrientsTitle} • ${t('temp_nutrient_solution')}`,
    [sensor_value_type_mapping.SENS_VAL_NUTRI_REDOX]: `${nutrientsTitle} • ${t('redox_current')}`,
    [sensor_value_type_mapping.SENS_VAL_NUTRI_DOS]: `${nutrientsTitle} • ${t('dos_current')}`
  };
  const selectedTypeLabel = typeToLabel[selectedType] ?? '---';

  // Value formatting by sensor type
  const valueFormatByType = {
    // Climate
    [sensor_value_type_mapping.SENS_VAL_RH]: { digits: 1, comp: FloatValue },
    [sensor_value_type_mapping.SENS_VAL_TEMP_AIR]: { digits: 1, comp: FloatValue },
    [sensor_value_type_mapping.SENS_VAL_LEAF_T]: { digits: 1, comp: FloatValue },
    [sensor_value_type_mapping.SENS_VAL_CO2]: { digits: 0, comp: NormalValue },
    // Root Zone
    [sensor_value_type_mapping.SENS_VAL_ROOT_VWC]: { digits: 1, comp: FloatValue },
    [sensor_value_type_mapping.SENS_VAL_ROOT_EC_PW]: { digits: 2, comp: FloatValue },
    [sensor_value_type_mapping.SENS_VAL_ROOT_TEMP]: { digits: 1, comp: FloatValue },
    // Nutrients
    [sensor_value_type_mapping.SENS_VAL_NUTRI_EC]: { digits: 2, comp: FloatValue },
    [sensor_value_type_mapping.SENS_VAL_NUTRI_PH]: { digits: 2, comp: FloatValue },
    [sensor_value_type_mapping.SENS_VAL_NUTRI_TEMP]: { digits: 1, comp: FloatValue },
    [sensor_value_type_mapping.SENS_VAL_NUTRI_REDOX]: { digits: 0, comp: NormalValue },
    [sensor_value_type_mapping.SENS_VAL_NUTRI_DOS]: { digits: 1, comp: FloatValue }
  };
  const vf = valueFormatByType[selectedType];
  const ValueComp = vf?.comp;

  const saveExcludeCurValue = (ex, event, value) => {
    let v = event.target.checked ? 0 : 1;
    let vv = { ...extReadsValues };
    // Find the actual index in the full array using unique identifiers
    const actualIdx = vv.extrs.findIndex(
      (item) =>
        item.type === ex.type &&
        item.sub_zone === ex.sub_zone &&
        item.gcl_addr === ex.gcl_addr &&
        item.port_id === ex.port_id &&
        item.addr_sensor === ex.addr_sensor
    );
    if (actualIdx === -1) {
      return;
    }
    vv.extrs[actualIdx].exclude_cur_value = v;
    setExtReadsValues(vv);
    extReadsControls.saveTempSettings(`extrs.${actualIdx}.exclude_cur_value`, v);
  };

  // address character 0-9, a-z or A-Z, a total of 62 available.
  function toSDI12Address(addr) {
    if (addr == null || addr < 0) {
      return '---';
    }

    // 48-57: ASCII codes for '0'-'9'
    if (addr >= 48 && addr <= 57) {
      return String.fromCharCode(addr);
    }

    // 97-122: ASCII codes for 'a'-'z'
    if (addr >= 97 && addr <= 122) {
      return String.fromCharCode(addr);
    }

    // 65-90: ASCII codes for 'A'-'Z'
    if (addr >= 65 && addr <= 90) {
      return String.fromCharCode(addr);
    }

    return '---';
  }

  function getSensorAddrDisplay(ex) {
    if (ex.sensor_addr_display_type === 0) {
      return toSDI12Address(ex.addr_sensor) ?? '---';
    }
    if (ex.sensor_addr_display_type === 1) {
      return ex.addr_sensor ?? '---';
    }
    return '---';
  }

  return (
    <DialogWithEnterAction
      open={open}
      onClose={handleClose}
      onEnterAction={
        write_access
          ? () => extReadsControls.saveSettings(transform_sent_settings, true)
          : handleClose
      }
      enterActionDisabled={write_access && connectionStatus !== 'good'}
      aria-labelledby="form-dialog-title"
    >
      <DialogTitle id="form-dialog-title">{t('single_sensor_readings')}</DialogTitle>
      <DialogTitle id="form-dialog-sub-title">{selectedTypeLabel}</DialogTitle>
      <DialogContent>
        <table
          style={{
            width: '100%',
            borderCollapse: 'separate',
            borderSpacing: '1em 0.4em'
          }}
        >
          <thead
            style={{
              fontSize: '1rem',
              fontWeight: 'normal',
              textTransform: 'capitalize'
            }}
          >
            <tr>
              <th style={{ color: '#f0f0f0' }}>{t('ext_read_value')}</th>
              <th style={{ color: '#f0f0f0' }}>{t('ext_read_slave_address')}</th>
              <th style={{ color: '#f0f0f0' }}>{t('ext_read_port')}</th>
              <th style={{ color: '#f0f0f0' }}>{t('ext_read_sensor_address')}</th>
              <th style={{ color: '#f0f0f0' }}>{t('ext_read_exclude')}</th>
            </tr>
          </thead>
          <tbody>
            {(() => {
              const rows = extReadsCurValues.extrs.filter(
                (ex) => ex.type === selectedType && ex.sub_zone === selectedSubZone
              );
              return rows.map((ex) => (
                <tr key={`${ex.addr_slave}-${ex.port}-${ex.addr_sensor}`}>
                  <td>
                    <ValueComp
                      name="ext_read_value"
                      value={ex.value ?? -1}
                      num_digits={vf.digits}
                      style={{
                        fontSize: 'inherit',
                        ...(ex.exclude_cur_value === 1 ? { color: 'grey' } : {})
                      }}
                    />
                  </td>
                  <td>{ex.gcl_addr ?? '---'}</td>
                  <td>{ex.port_id ?? '---'}</td>
                  <td>{getSensorAddrDisplay(ex)}</td>
                  <td>
                    {
                      <MuiSwitch
                        checked={
                          extReadsValues.extrs.find(
                            (fixed_ex) =>
                              fixed_ex.zone === ex.zone &&
                              fixed_ex.sub_zone === ex.sub_zone &&
                              fixed_ex.type === ex.type &&
                              fixed_ex.gcl_addr === ex.gcl_addr &&
                              fixed_ex.port_id === ex.port_id &&
                              fixed_ex.addr_sensor === ex.addr_sensor
                          )?.exclude_cur_value === 0
                        }
                        onChange={(event, value) => saveExcludeCurValue(ex, event, value)}
                        disabled={!write_access}
                        color="primary"
                      />
                    }
                  </td>
                </tr>
              ));
            })()}
          </tbody>
        </table>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} color="primary">
          {t('close')}
        </Button>
        {write_access && (
          <ActionButton
            title={connectionDisabledReason}
            onClick={() => {
              extReadsControls.saveSettings(transform_sent_settings, true);
            }}
            color="primary"
            disabled={connectionStatus !== 'good'}
          >
            {t('apply')}
          </ActionButton>
        )}
      </DialogActions>
    </DialogWithEnterAction>
  );
}

export function ZoneInfoContent({ controller, customizationScope = '', parent }) {
  const classes = useStyles();
  let { t } = useTranslation();
  const [mobile] = useState(isMobile);
  const { viewMode, setViewMode, selectorAvailable, showTable } = useMultiIOViewMode();
  const {
    key: tableSortKey,
    direction: tableSortDirection,
    requestSort: requestTableSort
  } = useMultiIOTableSort();

  const stateManager = getStateManager();
  const isSimulation =
    Boolean(controller.simulation) || stateManager.simulation?.controller?.id === controller.id;
  const isMultiIOController = controller.type === 6000 || controller.type === 5155;
  const getMultiIOInfoSortValue = (port, key) => {
    switch (key) {
      case 'type':
        return t(
          multiIOHwTypeMappping[port.type_id ?? port.type] ?? multiIOHwTypeMappping['default']
        );
      case 'memo':
        return port.memo ?? '';
      case 'function':
        return t(multiIOMapping[port.func] ?? multiIOMapping['default']);
      case 'state':
        return Number(port.state ?? -1);
      case 'io':
      default:
        return Number(port.id);
    }
  };
  const irrigationStatusZoneName = controller.zones ? parent.name : undefined;
  const irrigationErrorLabelMapping =
    controller.type === 5055 ? irrigationErrorMapping5055 : irrigationErrorMapping;
  const climateGraphs = useInfoClimateGraphs(parent.infos);
  const { humidityData, temperatureData: tempData } = useInfoChartData(climateGraphs);
  const humidityGraphValues = getValidGraphValues(humidityData.datasets[0].data);
  const tempGraphValues = getValidGraphValues(tempData.datasets[0].data);
  const humidityGraphBounds = getPaddedGraphBounds(humidityGraphValues, 15);
  const tempGraphBounds = getPaddedGraphBounds(tempGraphValues, 2);
  const humidityGraphMinimum = humidityGraphBounds?.minimum;
  const humidityGraphMaximum = humidityGraphBounds?.maximum;
  const tempGraphMinimum = tempGraphBounds?.minimum;
  const tempGraphMaximum = tempGraphBounds?.maximum;
  const options = useMemo(() => {
    const createOptions = (minimum, maximum) => ({
      responsive: true,
      maintainAspectRatio: false,
      defaultColors: ['#95c11e'],
      elements: {
        point: { radius: 0 },
        line: { backgroundColor: '#95c11e', borderColor: '#95c11e', width: '100%' }
      },
      scales: {
        //yAxes: [{ gridLines: { drawBorder: false } }],
        //xAxes: [{ gridLines: { display: false } }],
        x: {
          type: 'linear',
          bounds: 'data',
          offset: false,
          grid: { display: false, drawBorder: false },
          ticks: { display: false }
        },
        y: {
          min: minimum,
          max: maximum,
          grid: { display: false, drawBorder: false },
          ticks: { display: false }
        }
      },
      plugins: {
        legend: { display: false },
        tooltip: {
          callbacks: {
            title: (items) => {
              const item = items?.[0];
              const sourceCount = Number(parent.infos.info_graph_source_sample_count) || 0;
              const interval = Number(parent.infos.info_graph_sample_interval_seconds) || 0;
              return sourceCount > 0 && interval > 0
                ? formatGraphAge(item?.parsed?.x ?? 0, sourceCount, interval)
                : String(item?.label ?? '');
            }
          }
        }
      }
    });
    return {
      humidity: createOptions(humidityGraphMinimum, humidityGraphMaximum),
      temperature: createOptions(tempGraphMinimum, tempGraphMaximum)
    };
  }, [
    humidityGraphMaximum,
    humidityGraphMinimum,
    parent.infos,
    tempGraphMaximum,
    tempGraphMinimum
  ]);

  let cur_values = useSnapshot(parent.infos);
  const settings = useSnapshot(parent.settings);
  const controllerSnapshot = useSnapshot(controller);
  const tankCount = getNutritionTankInfo(controllerSnapshot, { settings }).length;
  const showTankInfo = tankCount > 0;
  const ec_fans = settings.ec_fans ?? {};
  const co2 = settings.co2 ?? {};
  // Info can render before system settings arrive from the controller.
  const vpdFormat = controller.settings.system?.vpd_format ?? 0;
  const vpdSetpoint = getVpdSetpoint(settings, cur_values.phase);
  const heatingMatSetpoint = getHeatingMatSetpoint(settings, cur_values.phase);
  const negativePressureSetpoint = getAvailableSettingNumber(settings.ventilation?.pressure_pa);
  const showRootZoneAndIrrigationInfo = !IsLegacyGrowBaseProWifiApi(controller);
  const showNutrientsInfo =
    isMultiIOController &&
    !supportsNutritionTanks(controllerSnapshot) &&
    !IsExcludedBasedOnTypeOrProject(controller, 'nutrients_hl', 'headlines');
  const measurementSourceZone = tankSourceZone(parent, isZoned(controller));
  const showCo2Info =
    isMultiIOController && !IsExcludedBasedOnTypeOrProject(controller, 'co2_hl', 'headlines');
  const { irrigation: irrigationShowCount, rootZone: rootZoneShowCount } =
    getRootZoneIrrigationShowCounts(controller, cur_values, isSimulation);
  const showRepeatedInfoSections =
    isZoned(controller) || irrigationShowCount > 1 || rootZoneShowCount > 1;
  const rootZoneIrrigationIndices = showRootZoneAndIrrigationInfo
    ? Array.from(
        { length: Math.min(4, Math.max(rootZoneShowCount, irrigationShowCount)) },
        (_, index) => index
      ).filter((index) => index === 0 || showRepeatedInfoSections)
    : [];
  const nutrientPanelCount = isZoned(controller)
    ? Math.min(4, Math.max(1, Number(cur_values.nutrients_show_cnt) || 1))
    : 1;
  const hvacPanelIndices = [
    cur_values.show_hvac_info,
    cur_values.show_hvac_info_2,
    cur_values.show_hvac_info_3
  ].reduce((indices, show, index) => (show === 1 ? [...indices, index] : indices), []);

  let multi_outs = makeMultiOutObject(controllerSnapshot, parent);
  let slaves = controller.zones ? controller.slaves : makeSlaveInfoObject(controller, parent);
  let sid = 'ext_readings';
  let ext_reads = makeExtReadingsObject(controller, parent);

  const write_access = controller.access_rights.ext_readings.write;

  const [extReadsValues, setExtReadsValues] = useState(ext_reads);

  const extReadsControls = settingControls(
    controller,
    parent,
    sid,
    extReadsValues,
    setExtReadsValues
  );

  const transform_sent_settings = (settings) => {
    let all_ext_reads = { ...ext_reads };
    all_ext_reads.extrs.map((ex) => {
      ex.exclude_cur_value = extReadsValues.extrs.find(
        (fixed_ex) =>
          fixed_ex.zone === ex.zone &&
          fixed_ex.sub_zone === ex.sub_zone &&
          fixed_ex.type === ex.type &&
          fixed_ex.gcl_addr === ex.gcl_addr &&
          fixed_ex.port_id === ex.port_id &&
          fixed_ex.addr_sensor === ex.addr_sensor
      )?.exclude_cur_value;
    });
    return all_ext_reads;
  };

  extReadsControls.resetSettings = () => {
    setExtReadsValues(ext_reads);
    delete controller.temp_settings[sid];
    writeTempSettings(controller);
  };

  const getExtReads = (type, subZone) =>
    ext_reads.extrs.filter((ex) => ex.type === type && ex.sub_zone === subZone).slice(0, 4); // limit to 4 readings

  const [extReadsDialogOpen, setExtReadsDialogOpen] = useState(false);
  const [selectedSensorSubZone, setSelectedSensorSubZone] = useState(null);
  const [selectedSensorType, setSelectedSensorType] = useState(null);

  const openExtReadsDialog = (type, subZone) => {
    setSelectedSensorType(type);
    setSelectedSensorSubZone(subZone);
    setExtReadsDialogOpen(true);
  };
  const closeExtReadsDialog = () => {
    extReadsControls.resetSettings();
    setExtReadsDialogOpen(false);
    setSelectedSensorType(null);
  };

  const infoGraphSpan = formatSelectedGraphSpan(
    cur_values.climate_sample_indices,
    Number(cur_values.info_graph_source_sample_count) || 0,
    Number(cur_values.info_graph_sample_interval_seconds) || 0
  );

  return (
    <CustomizableScope scope={customizationScope}>
      {/* External Readings Dialog */}
      <ExternalReadingsDialog
        open={extReadsDialogOpen}
        handleClose={closeExtReadsDialog}
        write_access={write_access}
        setExtReadsValues={setExtReadsValues}
        extReadsControls={extReadsControls}
        transform_sent_settings={transform_sent_settings}
        extReadsCurValues={ext_reads}
        extReadsValues={extReadsValues}
        selectedSubZone={selectedSensorSubZone}
        selectedType={selectedSensorType}
        tankName={
          showTankInfo &&
          (settings.irrigation_tank_info ?? []).find((tank) =>
            tankOwnsSource(tank, measurementSourceZone, selectedSensorSubZone)
          )?.name
        }
        controller={controller}
        mobile={mobile}
      />

      {/* Climate Values */}
      <CustomizableComponent
        controller={controller}
        parent={parent}
        id="climate_values"
        ext_id="edit_climate_values"
        visibilityIconPosition="heading-end"
        zIndex="-1"
      >
        <div style={{ flexWrap: 'wrap' }}>
          <SettingGroup controller={controller} hideLabel id={'climate_values'}>
            <Grid container spacing={0}>
              <CustomizableComponent
                controller={controller}
                parent={parent}
                id="info_humidity_sp"
                ext_id="edit_info_humidity"
              >
                <Grid item md={6} xs={12}>
                  <Box style={{ width: '100%' }}>
                    <InfoValueBox
                      controller={controller}
                      id={'info_humidity'}
                      label={<Desc id={'humidity'} />}
                      setpoint={`${t('setpoint_info')} ${
                        (cur_values.info_humidity_sp ?? -1) !== -1
                          ? parseFloat(cur_values.info_humidity_sp / 10).toFixed(1)
                          : 'n/a'
                      }`}
                      valueDetail={
                        isZoned(controller) ? (
                          <ExternalReadings
                            expand_id="info_ext_read_humidity"
                            readings={getExtReads(sensor_value_type_mapping.SENS_VAL_RH, 0)}
                            extReadsValues={extReadsValues}
                            namePrefix="ext_read_humidity_"
                            ValueComp={FloatValue}
                            num_digits={1}
                            marginProps={0}
                            valueProps={{ fontSize: '1.0rem' }}
                            onAdd={() =>
                              openExtReadsDialog(sensor_value_type_mapping.SENS_VAL_RH, 0)
                            }
                          />
                        ) : null
                      }
                      visualization={
                        humidityData.datasets[0].data?.length > 5 &&
                        humidityGraphValues.length > 0 ? (
                          <InfoGraphFrame
                            className={classes.climateGraphFrame}
                            maximum={Math.max(...humidityGraphValues).toFixed(1)}
                            minimum={Math.min(...humidityGraphValues).toFixed(1)}
                            span={infoGraphSpan}
                          >
                            <Box className={classes.graphData}>
                              <StableInfoLine data={humidityData} options={options.humidity} />
                            </Box>
                          </InfoGraphFrame>
                        ) : null
                      }
                    >
                      <FloatValue name="humidity" value={cur_values.humidity ?? -1} unit="% RH" />
                    </InfoValueBox>
                  </Box>
                </Grid>
              </CustomizableComponent>

              <CustomizableComponent
                controller={controller}
                parent={parent}
                id="info_temp_sp"
                ext_id="edit_info_temp"
              >
                <Grid item md={6} xs={12}>
                  <Box style={{ width: '100%' }}>
                    <InfoValueBox
                      controller={controller}
                      id={'info_temp'}
                      label={<Desc id={'temp'} />}
                      setpoint={`${t('setpoint_info')} ${
                        (cur_values.info_temp_sp ?? -1) !== -1
                          ? parseFloat(cur_values.info_temp_sp / 10).toFixed(1)
                          : 'n/a'
                      }`}
                      valueDetail={
                        isZoned(controller) ? (
                          <ExternalReadings
                            expand_id="info_ext_read_temp"
                            readings={getExtReads(sensor_value_type_mapping.SENS_VAL_TEMP_AIR, 0)}
                            extReadsValues={extReadsValues}
                            namePrefix="ext_read_temp_"
                            ValueComp={FloatValue}
                            num_digits={1}
                            marginProps={0}
                            valueProps={{ fontSize: '1.0rem' }}
                            onAdd={() =>
                              openExtReadsDialog(sensor_value_type_mapping.SENS_VAL_TEMP_AIR, 0)
                            }
                          />
                        ) : null
                      }
                      visualization={
                        tempData.datasets[0].data?.length > 5 &&
                        tempGraphValues.length > 0 ? (
                          <InfoGraphFrame
                            className={classes.climateGraphFrame}
                            maximum={Math.max(...tempGraphValues).toFixed(1)}
                            minimum={Math.min(...tempGraphValues).toFixed(1)}
                            span={infoGraphSpan}
                          >
                            <Box className={classes.graphData}>
                              <StableInfoLine data={tempData} options={options.temperature} />
                            </Box>
                          </InfoGraphFrame>
                        ) : null
                      }
                    >
                      <FloatValue name="temp" value={cur_values.temp ?? -1} unit="°C" />
                    </InfoValueBox>
                  </Box>
                </Grid>
              </CustomizableComponent>

              <CustomizableComponent
                controller={controller}
                parent={parent}
                id="info_vpd"
                ext_id="edit_info_vpd"
              >
                <Grid item md={4} xs={12} className={classes.compactClimateItem}>
                  <div style={{ display: 'flex', flexWrap: 'wrap', flexGrow: 1 }}>
                    <InfoValueBox
                      controller={controller}
                      id="info_vpd"
                      label={<Desc id={'vpd'} />}
                      setpoint={`${t('setpoint_info')} ${
                        vpdSetpoint != null
                          ? (
                              Number(vpdSetpoint) / 10 ** (vpdFormat === 0 ? 1 : 2)
                            ).toFixed(vpdFormat === 0 ? 1 : 2)
                          : '---'
                      } ${vpdFormat === 0 ? 'hPa' : 'kPa'}`}
                      setpointDisabled={Number(settings.climate?.vpd_calc_enable) <= 0}
                      style={{ paddingBottom: 16, paddingTop: 16 }}
                    >
                      <FloatValue
                        name="vpd"
                        value={cur_values.vpd ?? -1}
                        unit={vpdFormat === 0 ? 'hPa' : 'kPa'}
                        num_digits={vpdFormat === 0 ? 1 : 2}
                        mod_digits={vpdFormat === 0 ? 1 : 10}
                        style={{ lineHeight: 1.1 }}
                      />
                    </InfoValueBox>
                  </div>
                </Grid>
              </CustomizableComponent>

              <CustomizableComponent
                controller={controller}
                parent={parent}
                id="info_leaf_temp"
                ext_id="edit_info_temp_leaf_current"
              >
                <Grid item md={4} xs={12} className={classes.compactClimateItem}>
                  <InfoValueBox
                    controller={controller}
                    id="info_temp_leaf_current"
                    label={<Desc id={'temp_leaf_current'} />}
                    style={{ paddingBottom: 16, paddingTop: 16 }}
                    valueDetail={
                      isZoned(controller) ? (
                        <ExternalReadings
                          expand_id="info_ext_read_temp_leaf_current"
                          readings={getExtReads(sensor_value_type_mapping.SENS_VAL_LEAF_T, 0)}
                          extReadsValues={extReadsValues}
                          namePrefix="ext_read_temp_leaf_current_"
                          ValueComp={FloatValue}
                          num_digits={1}
                          marginProps={0}
                          valueProps={{ fontSize: '1.0rem' }}
                          onAdd={() =>
                            openExtReadsDialog(sensor_value_type_mapping.SENS_VAL_LEAF_T, 0)
                          }
                        />
                      ) : null
                    }
                  >
                    <FloatValue
                      name="temp_leaf_current"
                      value={cur_values.temp_leaf_current ?? -1}
                      unit="°C"
                      style={{ lineHeight: 1.1 }}
                    />
                  </InfoValueBox>
                </Grid>
              </CustomizableComponent>
              <CustomizableComponent
                controller={controller}
                parent={parent}
                id="info_co2"
                ext_id="edit_info_co2"
              >
                <Grid item md={4} xs={12} className={classes.compactClimateItem}>
                  <InfoValueBox
                    controller={controller}
                    id={'info_co2'}
                    label={<Desc id={'co2'} />}
                    setpoint={`${t('setpoint_info')} ${settings.co2?.co2_sp ?? '---'} ppm`}
                    setpointDisabled={Number(cur_values.phase) !== 2}
                    style={{ paddingBottom: 16, paddingTop: 16 }}
                    valueDetail={
                      isZoned(controller) ? (
                        <ExternalReadings
                          expand_id="info_ext_read_co2"
                          readings={getExtReads(sensor_value_type_mapping.SENS_VAL_CO2, 0)}
                          extReadsValues={extReadsValues}
                          namePrefix="ext_read_co2_"
                          ValueComp={NormalValue}
                          num_digits={0}
                          marginProps={0}
                          valueProps={{ fontSize: '1.0rem' }}
                          onAdd={() =>
                            openExtReadsDialog(sensor_value_type_mapping.SENS_VAL_CO2, 0)
                          }
                        />
                      ) : null
                    }
                  >
                    <NormalValue
                      name="co2"
                      value={cur_values.co2 ?? -1}
                      unit="ppm"
                      style={{ lineHeight: 1.1 }}
                    />
                  </InfoValueBox>
                </Grid>
              </CustomizableComponent>
            </Grid>
          </SettingGroup>
        </div>
      </CustomizableComponent>

      <InfoValueDashboard>
        {rootZoneIrrigationIndices.map((index) => (
          <RootZoneIrrigationPair
            key={`root-zone-irrigation-${index + 1}`}
            controller={controller}
            parent={parent}
            index={index}
            curValues={cur_values}
            extReadsValues={extReadsValues}
            getExtReads={getExtReads}
            irrigationErrorLabelMapping={irrigationErrorLabelMapping}
            irrigationStatusZoneName={irrigationStatusZoneName}
            openExtReadsDialog={openExtReadsDialog}
            renderGraphs
            showRootZone={rootZoneShowCount > index}
            showIrrigation={irrigationShowCount > index}
            paired={rootZoneShowCount > index && irrigationShowCount > index}
          />
        ))}

        {showNutrientsInfo && (
          <CustomizableInfoValueCollection
            controller={controller}
            id="nutrients_collection"
            parent={parent}
          >
            {Array.from({ length: nutrientPanelCount }, (_, index) => (
              <NutrientsInfoPanel
                key={`nutrients-${index + 1}`}
                controller={controller}
                parent={parent}
                settings={settings}
                index={index}
                curValues={cur_values}
                extReadsValues={extReadsValues}
                getExtReads={getExtReads}
                openExtReadsDialog={openExtReadsDialog}
              />
            ))}
          </CustomizableInfoValueCollection>
        )}

        {showTankInfo && (
          <NutritionTankInfo
            controller={controller}
            parent={parent}
            paired={tankCount > 1}
            renderMeasurementDetail={(tank, measurement) => {
              if (
                !isZoned(controller) ||
                !tankOwnsSource(tank, measurementSourceZone, tank.measurement_source_index)
              )
                return null;
              const index = tank.measurement_source_index;
              return (
                <ExternalReadings
                  expand_id={`tank_${tank.tank_id}_${measurement.key}`}
                  readings={getExtReads(measurement.type, index)}
                  extReadsValues={extReadsValues}
                  namePrefix={`tank_${tank.tank_id}_${measurement.key}_`}
                  ValueComp={measurement.digits ? FloatValue : NormalValue}
                  num_digits={measurement.digits}
                  marginProps={0}
                  valueProps={{ fontSize: '0.9rem' }}
                  onAdd={() => openExtReadsDialog(measurement.type, index)}
                />
              );
            }}
          />
        )}

        {hvacPanelIndices.map((index) => (
          <CustomizableInfoValueCollection
            controller={controller}
            id={`hvac_collection_${index + 1}`}
            key={`hvac-${index + 1}`}
            parent={parent}
          >
            <HvacInfoPanel
              controller={controller}
              parent={parent}
              index={index}
              curValues={cur_values}
            />
          </CustomizableInfoValueCollection>
        ))}
        {showCo2Info && (
          <CustomizableInfoValueCollection
            controller={controller}
            id="co2_collection"
            parent={parent}
          >
            <Co2InfoPanel controller={controller} parent={parent} values={co2} />
          </CustomizableInfoValueCollection>
        )}

        {isZoned(controller) &&
          renderAhuInfoCollections({
            controller,
            parent,
            storageScope: customizationScope
          })}
      </InfoValueDashboard>

      {/* More */}
      <CustomizableComponent
        controller={controller}
        parent={parent}
        id="more"
        zIndex="-1"
        ext_id="edit_more"
        visibilityIconPosition="heading-end"
      >
        <SettingGroup label={t('more')}>
          <InfoValueList>
            <InfoValueGroup title={t('sensors')}>
              <CustomizableInfoValueRow
                controller={controller}
                parent={parent}
                id="rh_intake_current"
                extId="edit_info_rh_intake_current"
                label={<Desc id="rh_intake_current" show="humidity_intake" />}
              >
                <FloatValue
                  name="rh_intake_current"
                  value={cur_values.rh_intake_current ?? -1}
                  unit="% RH"
                />
              </CustomizableInfoValueRow>
              <CustomizableInfoValueRow
                controller={controller}
                parent={parent}
                id="temp_intake_current"
                extId="edit_info_temp_intake_current"
                label={<Desc id="temp_intake_current" show="temp_intake" />}
              >
                <FloatValue
                  name="temp_intake_current"
                  value={cur_values.temp_intake_current ?? -1}
                  unit="°C"
                />
              </CustomizableInfoValueRow>
              <CustomizableInfoValueRow
                controller={controller}
                parent={parent}
                id="temp_heating_mat"
                extId="edit_info_temp_heating_mat"
                label={<Desc id="temp_heating_mat" />}
                setpoint={
                  heatingMatSetpoint != null && Number.isFinite(heatingMatSetpoint)
                    ? `${t('setpoint_info')} ${heatingMatSetpoint.toFixed(1)} °C`
                    : null
                }
              >
                <FloatValue
                  name="temp_heating_mat"
                  value={cur_values.temp_heating_mat ?? -1}
                  unit="°C"
                />
              </CustomizableInfoValueRow>
              <CustomizableInfoValueRow
                controller={controller}
                parent={parent}
                id="neg_pressure"
                extId="edit_info_neg_pressure"
                label={<Desc id="neg_pressure" />}
                setpoint={
                  Number.isFinite(negativePressureSetpoint)
                    ? `${t('setpoint_info')} ${negativePressureSetpoint} Pa`
                    : null
                }
              >
                <NegativeValue
                  name="neg_pressure"
                  value={cur_values.neg_pressure ?? -1}
                  unit="Pa"
                />
              </CustomizableInfoValueRow>
            </InfoValueGroup>

            <InfoValueGroup title={t('timing')}>
              <CustomizableInfoValueRow
                controller={controller}
                parent={parent}
                id="info_interval_timer"
                extId="edit_info_interval_timer"
                label={<Desc id="info_interval_timer" />}
              >
                <span name="info_interval_timer">
                  <NormalValue
                    name="onoffswitch"
                    value={t(
                      offOnMapping[cur_values.info_interval_timer_active ?? -1] ??
                        offOnMapping.default
                    )}
                    unit=""
                  />{' '}
                  in{' '}
                  <NormalValue
                    name="intervaltimer"
                    value={cur_values.info_interval_timer ?? -1}
                    unit=""
                  />
                </span>
              </CustomizableInfoValueRow>
              <CustomizableInfoValueRow
                controller={controller}
                parent={parent}
                id="info_interval_2_timer"
                extId="edit_info_interval_2_timer"
                label={<Desc id="info_interval_2_timer" />}
              >
                <span name="info_interval_2_timer">
                  <NormalValue
                    name="onoffswitch"
                    value={t(
                      offOnMapping[cur_values.info_interval_2_timer_active ?? -1] ??
                        offOnMapping.default
                    )}
                    unit=""
                  />{' '}
                  in{' '}
                  <NormalValue
                    name="intervaltimer"
                    value={cur_values.info_interval_2_timer}
                    unit=""
                  />
                </span>
              </CustomizableInfoValueRow>
              <CustomizableInfoValueRow
                controller={controller}
                parent={parent}
                id="time"
                extId="edit_info_time"
                label={<Desc id="time" />}
              >
                <NormalValue name="time" value={cur_values.time ?? ''} unit="" />
              </CustomizableInfoValueRow>
              <CustomizableInfoValueRow
                controller={controller}
                parent={parent}
                id="date"
                extId="edit_info_date"
                label={<Desc id="date" />}
              >
                <NormalValue name="date" value={cur_values.date ?? ''} unit="" />
              </CustomizableInfoValueRow>
              <CustomizableInfoValueRow
                controller={controller}
                parent={parent}
                id="phase"
                extId="edit_info_phase"
                label={<Desc id="phase" />}
              >
                <NormalValue
                  name="phasevalue"
                  value={t(dayNightCo2PreheatMapping[cur_values.phase ?? -1])}
                  unit=""
                />
              </CustomizableInfoValueRow>
            </InfoValueGroup>

            <InfoValueGroup title={t('outputs')}>
              <CustomizableInfoValueRow
                controller={controller}
                parent={parent}
                id="info_lights"
                extId="edit_info_lights"
                label={<Desc id="info_lights" />}
              >
                <NormalValue name="info_lights" value={cur_values.info_lights ?? -1} unit="%" />
              </CustomizableInfoValueRow>
              {isZoned(controller) && cur_values.lights_dim_show_cnt > 1 && (
                <CustomizableInfoValueRow
                  controller={controller}
                  parent={parent}
                  id="lights_dim_val_current_2"
                  extId="edit_lights_dim_val_current_2"
                  label={<Desc id="lights_dim_val_current_2" />}
                >
                  <NormalValue
                    name="lights_dim_val_current_2"
                    value={cur_values.lights_dim_val_current_2 ?? -1}
                    unit="%"
                  />
                </CustomizableInfoValueRow>
              )}
              {isZoned(controller) && cur_values.lights_dim_show_cnt > 2 && (
                <CustomizableInfoValueRow
                  controller={controller}
                  parent={parent}
                  id="lights_dim_val_current_3"
                  extId="edit_lights_dim_val_current_3"
                  label={<Desc id="lights_dim_val_current_3" />}
                >
                  <NormalValue
                    name="lights_dim_val_current_3"
                    value={cur_values.lights_dim_val_current_3 ?? -1}
                    unit="%"
                  />
                </CustomizableInfoValueRow>
              )}
              {isZoned(controller) && cur_values.lights_dim_show_cnt > 3 && (
                <CustomizableInfoValueRow
                  controller={controller}
                  parent={parent}
                  id="lights_dim_val_current_4"
                  extId="edit_lights_dim_val_current_4"
                  label={<Desc id="lights_dim_val_current_4" />}
                >
                  <NormalValue
                    name="lights_dim_val_current_4"
                    value={cur_values.lights_dim_val_current_4 ?? -1}
                    unit="%"
                  />
                </CustomizableInfoValueRow>
              )}
              <CustomizableInfoValueRow
                controller={controller}
                parent={parent}
                id="temp_heater_setpoint_info"
                extId="edit_info_temp_heater_setpoint_info"
                label={<Desc id="temp_heater_setpoint_info" />}
              >
                {(cur_values.temp_heater_setpoint_info ?? -1) > 49 ? (
                  <FloatValue
                    name="temp_heater_setpoint_info"
                    value={cur_values.temp_heater_setpoint_info ?? -1}
                    unit="°C"
                  />
                ) : (
                  <NormalValue name="phasevalue" value={t('off')} unit="" />
                )}
              </CustomizableInfoValueRow>
              <CustomizableInfoValueRow
                controller={controller}
                parent={parent}
                id="ec_fans_intake"
                extId="edit_info_ec_fans_intake"
                label={<Desc id="ec_fans_intake" />}
              >
                <NormalValue
                  name="ec_fans_intake_out"
                  value={ec_fans.ec_fans_intake_out ?? -1}
                  unit="% "
                />
                <NormalValue
                  name="ec_fans_intake_rpm"
                  value={ec_fans.ec_fans_intake_rpm ?? -1}
                  unit="rpm"
                />
              </CustomizableInfoValueRow>
              <CustomizableInfoValueRow
                controller={controller}
                parent={parent}
                id="ec_fans_exhaust"
                extId="edit_info_ec_fans_exhaust"
                label={<Desc id="ec_fans_exhaust" />}
              >
                <NormalValue
                  name="ec_fans_exhaust_out"
                  value={ec_fans.ec_fans_exhaust_out ?? -1}
                  unit="% "
                />
                <NormalValue
                  name="ec_fans_exhaust_rpm"
                  value={ec_fans.ec_fans_exhaust_rpm ?? -1}
                  unit="rpm"
                />
              </CustomizableInfoValueRow>
            </InfoValueGroup>
          </InfoValueList>
        </SettingGroup>
      </CustomizableComponent>

      <CustomizableComponent
        controller={controller}
        parent={parent}
        id="info_multi_outs"
        ext_id=""
        visibilityIconPosition="heading-end"
        zIndex="-1"
      >
        <div style={{ flexWrap: 'wrap' }}>
          <SettingGroup
            label={
              controller.type === 6000 || controller.type === 5155
                ? t('multifunctional_ios_heading') +
                  ' (' +
                  Object.values(multi_outs).reduce(
                    (sum, slave) =>
                      sum +
                      Object.values(slave).filter(
                        (io) =>
                          isMultiIOInfoVisible(io, controller)
                      ).length,
                    0
                  ) +
                  ')'
                : t('multifunctional_outputs_heading') +
                  ' (' +
                  Object.values(multi_outs).filter((mo) => mo.func !== 19 && mo.func != null)
                    .length +
                  ')'
            }
          >
            {isMultiIOController && (
              <MultiIOViewToggle
                viewMode={viewMode}
                onChange={setViewMode}
                hidden={!selectorAvailable}
              />
            )}
            {isMultiIOController && showTable ? (
              <>
                {Object.keys(multi_outs).map((slave) => {
                  const activePorts = Object.values(multi_outs[slave]).filter(
                    (mo) =>
                      isMultiIOInfoVisible(mo, controller)
                  );
                  const sortedActivePorts = sortMultiIORows(
                    activePorts,
                    tableSortKey,
                    tableSortDirection,
                    getMultiIOInfoSortValue
                  );

                  return (
                    <div
                      key={`multi-out-table-slave-${slave}`}
                      className={classes.multiOutBox}
                      style={{ padding: '1em', width: '100%', marginBottom: '0.5em' }}
                    >
                      <span style={{ fontSize: '1.5em', display: 'flex', alignItems: 'center' }}>
                        {t('slave_address_hl')}{' '}
                        <span style={{ fontFamily: 'monospace', marginLeft: '0.5em' }}>
                          {slave.toString()}
                        </span>
                        <ConnectionStatus
                          controllerStatus={controller?.connection?.status}
                          slave={Object.values(slaves).find(
                            (item) => item.address === parseInt(slave)
                          )}
                        />
                      </span>
                      <MultiIOTable
                        ariaLabel={t('multifunctional_ios_heading')}
                        minWidth={900}
                        sortKey={tableSortKey}
                        sortDirection={tableSortDirection}
                        onSort={requestTableSort}
                        columns={[
                          {
                            key: 'function',
                            label: t('function'),
                            width: '26%'
                          },
                          {
                            key: 'state',
                            label: `${t('multi_io_state')} / ${t('info')}`,
                            width: '18%'
                          },
                          { key: 'memo', label: t('custom_memo_name'), width: '20%' },
                          { key: 'io', label: MULTI_IO_SHORT_LABEL, width: '16%' },
                          { key: 'type', label: t('type'), width: '20%' }
                        ]}
                      >
                        {sortedActivePorts.map((mo) => (
                          <MultiIOInfoTableRow
                            key={`multi-out-table-${slave}-${mo.id}`}
                            controller={controllerSnapshot}
                            port={mo}
                            slaveAddress={slave}
                          />
                        ))}
                        {activePorts.length === 0 && (
                          <TableRow>
                            <TableCell colSpan={5}>
                              <i>{t('no active multi outs available')}</i>
                            </TableCell>
                          </TableRow>
                        )}
                      </MultiIOTable>
                    </div>
                  );
                })}
              </>
            ) : (
              <Grid container spacing={0}>
                {controller.type >= 6000 || controller.type === 5155 ? (
                  <>
                    {Object.keys(multi_outs).map((slave) => (
                      <div
                        key={`multi-out-slave-${slave}`}
                        className={classes.multiOutBox}
                        style={{ padding: '1em', width: '100%', marginBottom: '0.5em' }}
                      >
                        <span style={{ fontSize: '1.5em', display: 'flex', alignItems: 'center' }}>
                          {' '}
                          {t('slave_address_hl')}{' '}
                          <span style={{ fontFamily: 'monospace', marginLeft: '0.5em' }}>
                            {slave.toString()}
                          </span>
                          <ConnectionStatus
                            controllerStatus={controller?.connection?.status}
                            slave={Object.values(slaves).find((s) => s.address === parseInt(slave))}
                          />
                        </span>

                        <Grid container spacing={1}>
                          {Object.values(multi_outs[slave]).map(
                            (mo) =>
                              isMultiIOInfoVisible(mo, controller) && (
                                <Grid
                                  key={`multi-out-${slave}-${mo.id}`}
                                  item
                                  xs={12}
                                  sm={6}
                                  md={4}
                                  lg={3}
                                  style={{ display: 'flex' }}
                                >
                                  <MultiIOInfoCard
                                    controller={controller}
                                    index={getPhysicalIOLabel(controllerSnapshot, slave, mo.id)}
                                    memo={mo.memo ?? ''}
                                    func={mo.func ?? ''}
                                    funcIndex={mo.func_index ?? ''}
                                    value={mo.state ?? ''}
                                    type={controller.zones ? (mo.type_id ?? '') : (mo.type ?? '')}
                                    monitor={mo.monitor}
                                    info={mo.info ?? ''}
                                    infoType={mo.info_type ?? mo.type ?? ''}
                                    overwriteSelect={mo.overwr_select}
                                    overwriteValue={mo.overwr_val_ana}
                                  />
                                </Grid>
                              )
                          )}
                          {Object.values(multi_outs[slave]).filter(
                            (mo) =>
                              isMultiIOInfoVisible(mo, controller)
                          ).length === 0 && <i>{t('no active multi outs available')}</i>}
                        </Grid>
                      </div>
                    ))}
                  </>
                ) : (
                  <>
                    {Object.values(multi_outs).map(
                      (mo) =>
                        mo.func !== 19 &&
                        mo.func != null && (
                          <Grid
                            key={`multi-out-${mo.id}`}
                            item
                            md={3}
                            xs={12}
                            style={{ display: 'flex' }}
                          >
                            <div style={{ display: 'flex', flexWrap: 'wrap', flexGrow: 1 }}>
                              <ValueBox controller={controller} id={'multi_out_elem_' + mo.id}>
                                <MultiOutHeading
                                  controller={controller}
                                  parent={parent}
                                  index={mo.id}
                                  memo={mo.memo ?? ''}
                                />
                                <MultiOutLabel
                                  controller={controller}
                                  parent={parent}
                                  func={mo.func ?? ''}
                                  value={mo.state ?? ''}
                                />
                              </ValueBox>
                            </div>
                          </Grid>
                        )
                    )}
                  </>
                )}
              </Grid>
            )}
          </SettingGroup>
        </div>
      </CustomizableComponent>
    </CustomizableScope>
  );
}

export default function Info() {
  const params = useParams();
  const stateManager = getStateManager();
  const { controller, parent } = stateManager.getControllerForTab(params);

  return <ZoneInfoContent controller={controller} parent={parent} />;
}
